import { useEffect, useState } from "react";
import axios from "axios";
import { Search, Loader2, Image as ImageIcon, Moon, Sun, Heart, FolderOpen, Home, BarChart3, Filter, Download, CheckSquare, Square, Tag as TagIcon, Settings as SettingsIcon, ChevronDown, ChevronUp, X, FolderPlus, Folder } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useTheme } from "@/contexts/ThemeContext";
import NotificationBanner from "@/components/NotificationBanner";
import { processImageUrlSync } from "@/utils/imageProxy";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get state from URL or use defaults
  const urlTab = searchParams.get('tab') || 'misskon';
  const urlPage = parseInt(searchParams.get('page')) || 1;
  const urlSearch = searchParams.get('search') || '';
  const urlSortBy = searchParams.get('sortBy') || 'created_at';
  const urlSortOrder = searchParams.get('sortOrder') || 'desc';
  const urlFilterTag = searchParams.get('tag') || '';
  const urlFavoritesOnly = searchParams.get('favorites') === 'true';
  
  // State synchronized with URL
  const [activeTab, setActiveTab] = useState(urlTab);
  const [page, setPage] = useState(urlPage);
  const [searchQuery, setSearchQuery] = useState(urlSearch);
  const [isSearchMode, setIsSearchMode] = useState(!!urlSearch);
  const [sortBy, setSortBy] = useState(urlSortBy);
  const [sortOrder, setSortOrder] = useState(urlSortOrder);
  const [filterTag, setFilterTag] = useState(urlFilterTag);
  const [favoritesOnly, setFavoritesOnly] = useState(urlFavoritesOnly);
  
  // Local state (not in URL)
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nsfwMode, setNsfwMode] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const [selectedArticles, setSelectedArticles] = useState([]);
  const [bulkMode, setBulkMode] = useState(false);
  const [collections, setCollections] = useState([]);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [crawlStatus, setCrawlStatus] = useState({ status: 'idle', progress: 0, articles_count: 0 });
  const [pollingInterval, setPollingInterval] = useState(null);
  const [columnCount, setColumnCount] = useState(4); // Default 4 columns
  const [stickyTabBar, setStickyTabBar] = useState(true); // Default sticky enabled
  
  // Create new collection states  
  const [showCreateCollectionDialog, setShowCreateCollectionDialog] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState("");
  const [newCollectionDesc, setNewCollectionDesc] = useState("");
  
  // Quick add to collection states
  const [quickAddCollectionArticleId, setQuickAddCollectionArticleId] = useState(null);
  const [showQuickAddDialog, setShowQuickAddDialog] = useState(false);
  const [showCreateQuickCollectionDialog, setShowCreateQuickCollectionDialog] = useState(false);
  const [quickCollectionName, setQuickCollectionName] = useState("");
  const [quickCollectionDesc, setQuickCollectionDesc] = useState("");
  
  // Helper function to update URL params
  const updateURLParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '' || value === false) {
        newParams.delete(key);
      } else {
        newParams.set(key, value.toString());
      }
    });
    
    setSearchParams(newParams, { replace: true });
  };

  // Initialize on mount
  useEffect(() => {
    const savedNsfw = localStorage.getItem("nsfwMode") === "true";
    setNsfwMode(savedNsfw);
    fetchTags();
    fetchCollections();
    loadSettings();
  }, []);

  // Load settings (column count, sticky tab bar, and image hover preview)
  const loadSettings = async () => {
    try {
      const [columnResponse, stickyResponse] = await Promise.all([
        axios.get(`${API}/settings/column_count`),
        axios.get(`${API}/settings/sticky_tab_bar`)
      ]);
      
      if (columnResponse.data.value) {
        setColumnCount(parseInt(columnResponse.data.value));
      }
      
      if (stickyResponse.data.value !== undefined) {
        setStickyTabBar(stickyResponse.data.value === "true");
      }
    } catch (error) {
      console.error("Error loading settings:", error);
      // Use defaults if loading fails
    }
  };

  // Sync state from URL params and fetch articles when URL changes
  useEffect(() => {
    const tab = searchParams.get('tab') || 'misskon';
    const pageNum = parseInt(searchParams.get('page')) || 1;
    const search = searchParams.get('search') || '';
    const sortByParam = searchParams.get('sortBy') || 'created_at';
    const sortOrderParam = searchParams.get('sortOrder') || 'desc';
    const tagParam = searchParams.get('tag') || '';
    const favoritesParam = searchParams.get('favorites') === 'true';
    
    // Update state from URL
    setActiveTab(tab);
    setPage(pageNum);
    setSearchQuery(search);
    setIsSearchMode(!!search);
    setSortBy(sortByParam);
    setSortOrder(sortOrderParam);
    setFilterTag(tagParam);
    setFavoritesOnly(favoritesParam);
    
    // Fetch articles based on URL state (without updating URL again)
    const fetchFromURL = async () => {
      setLoading(true);
      try {
        if (search) {
          // Search mode
          const response = await axios.post(`${API}/search`, {
            query: search,
            source: tab,
            page: pageNum,
          });
          setArticles(response.data);
        } else {
          // Browse mode
          const params = new URLSearchParams();
          params.append("source", tab);
          params.append("page", pageNum);
          params.append("sort_by", sortByParam);
          params.append("sort_order", sortOrderParam);
          if (tagParam) params.append("tag", tagParam);
          if (favoritesParam) params.append("favorites_only", "true");
          
          const response = await axios.get(`${API}/articles?${params.toString()}`);
          setArticles(response.data);
        }
        
        // Scroll to top after content is loaded
        // Use setTimeout to ensure DOM has updated
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
        
      } catch (error) {
        console.error("Error fetching articles from URL:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchFromURL();
  }, [searchParams]); // Re-run when URL changes (back/forward navigation)


  const fetchTags = async () => {
    try {
      const response = await axios.get(`${API}/tags`);
      setAllTags(response.data.tags);
    } catch (error) {
      console.error("Error fetching tags:", error);
    }
  };

  const fetchCollections = async () => {
    try {
      const response = await axios.get(`${API}/collections`);
      setCollections(response.data);
    } catch (error) {
      console.error("Error fetching collections:", error);
    }
  };

  // Helper function to navigate to article with preserved state
  const navigateToArticle = (articleId) => {
    // Build return path with current state
    const currentParams = new URLSearchParams();
    currentParams.set('tab', activeTab);
    currentParams.set('page', page);
    if (searchQuery) currentParams.set('search', searchQuery);
    if (sortBy !== 'created_at') currentParams.set('sortBy', sortBy);
    if (sortOrder !== 'desc') currentParams.set('sortOrder', sortOrder);
    if (filterTag) currentParams.set('tag', filterTag);
    if (favoritesOnly) currentParams.set('favorites', 'true');
    
    const returnPath = `/?${currentParams.toString()}`;
    
    navigate(`/article/${articleId}`, { 
      state: { returnPath } 
    });
  };

  const handleNsfwToggle = (checked) => {
    setNsfwMode(checked);
    localStorage.setItem("nsfwMode", checked.toString());
  };

  const fetchArticles = async (source, pageNum = 1, triggerCrawl = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("source", source);
      params.append("page", pageNum);
      params.append("sort_by", sortBy);
      params.append("sort_order", sortOrder);
      if (filterTag) {
        params.append("tag", filterTag);
      }
      if (favoritesOnly) {
        params.append("favorites_only", "true");
      }
      
      // For 06se or when explicitly requested, trigger background crawl
      if (triggerCrawl || (source === '06se' && pageNum === 1)) {
        params.append("trigger_crawl", "true");
      }

      const response = await axios.get(`${API}/articles?${params.toString()}`);
      setArticles(response.data);
      
      // Update URL params
      updateURLParams({
        tab: source,
        page: pageNum,
        search: searchQuery || null,
        sortBy: sortBy !== 'created_at' ? sortBy : null,
        sortOrder: sortOrder !== 'desc' ? sortOrder : null,
        tag: filterTag || null,
        favorites: favoritesOnly || null
      });
      
      // Start polling if crawling for 06se
      if (source === '06se' && pageNum === 1) {
        startCrawlPolling(source);
        if (response.data.length === 0) {
          toast.info("Đang crawl articles từ 06se.com...");
        } else {
          toast.success(`Hiển thị ${response.data.length} articles, đang crawl thêm...`);
        }
      } else {
        toast.success(`Loaded ${response.data.length} articles`);
      }
    } catch (error) {
      console.error("Error fetching articles:", error);
      toast.error("Failed to fetch articles");
    } finally {
      setLoading(false);
    }
  };
  
  // Poll crawl status
  const startCrawlPolling = (source) => {
    // Clear existing interval
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }
    
    const interval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/crawl-status/${source}`);
        setCrawlStatus(response.data);
        
        // If completed or error, refresh articles and stop polling
        if (response.data.status === 'completed' || response.data.status === 'error') {
          clearInterval(interval);
          setPollingInterval(null);
          
          if (response.data.status === 'completed') {
            toast.success(`Crawl hoàn tất! ${response.data.articles_count} articles mới`);
            // Refresh articles without triggering another crawl
            fetchArticles(source, page, false);
          } else if (response.data.error) {
            toast.error(`Crawl error: ${response.data.error}`);
          }
        }
      } catch (error) {
        console.error("Error polling crawl status:", error);
      }
    }, 3000); // Poll every 3 seconds
    
    setPollingInterval(interval);
  };
  
  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error("Please enter a search query");
      return;
    }

    setLoading(true);
    setIsSearchMode(true);
    try {
      const response = await axios.post(`${API}/search`, {
        query: searchQuery,
        source: activeTab,
        page: 1,
      });
      setArticles(response.data);
      setPage(1);
      
      // Update URL
      updateURLParams({
        tab: activeTab,
        page: 1,
        search: searchQuery,
        sortBy: sortBy !== 'created_at' ? sortBy : null,
        sortOrder: sortOrder !== 'desc' ? sortOrder : null,
        tag: filterTag || null,
        favorites: favoritesOnly || null
      });
      
      toast.success(`Found ${response.data.length} results for "${searchQuery}"`);
    } catch (error) {
      console.error("Error searching:", error);
      toast.error("Search failed");
    } finally {
      setLoading(false);
    }
  };

  // Clear search and return to browse mode
  const clearSearch = () => {
    setSearchQuery("");
    setIsSearchMode(false);
    setPage(1);
    updateURLParams({ search: null });
    fetchArticles(activeTab, 1);
  };

  const handleNextPage = async () => {
    const nextPage = page + 1;
    setPage(nextPage);
    
    if (isSearchMode) {
      // Search mode - load next page of search results
      await handleSearchPage(nextPage);
    } else {
      // Browse mode - load next page of homepage articles
      await fetchArticles(activeTab, nextPage);
    }
    
    // Scroll to top after content is loaded
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handlePrevPage = async () => {
    if (page <= 1) return;
    const prevPage = page - 1;
    setPage(prevPage);
    
    if (isSearchMode) {
      // Search mode - load previous page of search results
      await handleSearchPage(prevPage);
    } else {
      // Browse mode - load previous page of homepage articles
      await fetchArticles(activeTab, prevPage);
    }
    
    // Scroll to top after content is loaded
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleSearchPage = async (pageNum) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/search`, {
        query: searchQuery,
        source: activeTab,
        page: pageNum,
      });
      setArticles(response.data);
      
      // Update URL
      updateURLParams({
        tab: activeTab,
        page: pageNum,
        search: searchQuery,
        sortBy: sortBy !== 'created_at' ? sortBy : null,
        sortOrder: sortOrder !== 'desc' ? sortOrder : null,
        tag: filterTag || null,
        favorites: favoritesOnly || null
      });
      
      toast.success(`Loaded page ${pageNum}`);
    } catch (error) {
      console.error("Error searching:", error);
      toast.error("Search failed");
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = async (articleId, e) => {
    e.stopPropagation();
    try {
      const response = await axios.post(`${API}/favorites/toggle/${articleId}`);
      toast.success(response.data.message);
      
      // Update local state
      const updatedArticles = articles.map(a => 
        a.id === articleId ? { ...a, is_favorite: response.data.is_favorite } : a
      );
      setArticles(updatedArticles);
    } catch (error) {
      console.error("Error toggling favorite:", error);
      toast.error("Failed to update favorite");
    }
  };

  const toggleArticleSelection = (articleId) => {
    setSelectedArticles(prev => {
      if (prev.includes(articleId)) {
        return prev.filter(id => id !== articleId);
      } else {
        return [...prev, articleId];
      }
    });
  };

  const selectAllArticles = () => {
    if (selectedArticles.length === articles.length) {
      setSelectedArticles([]);
    } else {
      setSelectedArticles(articles.map(a => a.id));
    }
  };

  const bulkAddToCollection = async (collectionId) => {
    if (collectionId === "create_new") {
      setShowCreateCollectionDialog(true);
      return;
    }
    
    if (selectedArticles.length === 0) {
      toast.error("No articles selected");
      return;
    }

    try {
      const response = await axios.post(`${API}/bulk/collections/add`, {
        article_ids: selectedArticles,
        collection_id: collectionId
      });
      toast.success(response.data.message);
      setSelectedArticles([]);
      setBulkMode(false);
    } catch (error) {
      console.error("Error adding to collection:", error);
      toast.error("Failed to add to collection");
    }
  };
  
  // Quick add single article to collection
  const quickAddToCollection = async (collectionId) => {
    if (collectionId === "create_new") {
      // Show create new collection input
      setShowQuickAddDialog(false);
      setShowCreateQuickCollectionDialog(true);
      return;
    }
    
    if (!quickAddCollectionArticleId) {
      toast.error("No article selected");
      return;
    }

    try {
      await axios.post(`${API}/collections/add`, {
        article_id: quickAddCollectionArticleId,
        collection_id: collectionId
      });
      
      const collection = collections.find(c => c.id === collectionId);
      toast.success(`Added to collection "${collection?.name || 'Unknown'}"`);
      
      // Refresh articles to show updated collections
      fetchArticles(activeTab, page);
      
      setShowQuickAddDialog(false);
      setQuickAddCollectionArticleId(null);
    } catch (error) {
      console.error("Error adding to collection:", error);
      toast.error(error.response?.data?.detail || "Failed to add to collection");
    }
  };
  
  const createBulkCollection = async () => {
    if (!newCollectionName.trim()) {
      toast.error("Please enter a collection name");
      return;
    }
    
    if (selectedArticles.length === 0) {
      toast.error("No articles selected");
      return;
    }
    
    try {
      // Create the collection
      const response = await axios.post(`${API}/collections`, {
        name: newCollectionName,
        description: newCollectionDesc
      });
      
      const newCollection = response.data;
      toast.success(`Collection "${newCollectionName}" created`);
      
      // Add selected articles to the new collection
      await axios.post(`${API}/bulk/collections/add`, {
        article_ids: selectedArticles,
        collection_id: newCollection.id
      });
      
      toast.success(`${selectedArticles.length} articles added to new collection`);
      
      // Reset states
      setShowCreateCollectionDialog(false);
      setNewCollectionName("");
      setNewCollectionDesc("");
      setSelectedArticles([]);
      setBulkMode(false);
      
      // Refresh collections list
      fetchCollections();
    } catch (error) {
      console.error("Error creating collection:", error);
      toast.error(error.response?.data?.detail || "Failed to create collection");
    }
  };
  
  const createQuickCollection = async () => {
    if (!quickCollectionName.trim()) {
      toast.error("Please enter a collection name");
      return;
    }
    
    if (!quickAddCollectionArticleId) {
      toast.error("No article selected");
      return;
    }
    
    try {
      // Create the collection
      const response = await axios.post(`${API}/collections`, {
        name: quickCollectionName,
        description: quickCollectionDesc
      });
      
      const newCollection = response.data;
      toast.success(`Collection "${quickCollectionName}" created`);
      
      // Add article to the new collection
      await axios.post(`${API}/collections/add`, {
        article_id: quickAddCollectionArticleId,
        collection_id: newCollection.id
      });
      
      toast.success("Article added to new collection");
      
      // Reset states
      setShowCreateQuickCollectionDialog(false);
      setQuickCollectionName("");
      setQuickCollectionDesc("");
      setQuickAddCollectionArticleId(null);
      
      // Refresh collections and articles
      fetchCollections();
      fetchArticles(activeTab, page);
    } catch (error) {
      console.error("Error creating collection:", error);
      toast.error(error.response?.data?.detail || "Failed to create collection");
    }
  };


  const bulkAddTag = async () => {
    if (selectedArticles.length === 0) {
      toast.error("No articles selected");
      return;
    }

    const tag = prompt("Enter tag name:");
    if (!tag) return;

    try {
      const response = await axios.post(`${API}/bulk/tags/add`, {
        article_ids: selectedArticles,
        tag: tag
      });
      toast.success(response.data.message);
      setSelectedArticles([]);
      setBulkMode(false);
      fetchArticles(activeTab, page);
      fetchTags();
    } catch (error) {
      console.error("Error adding tags:", error);
      toast.error("Failed to add tags");
    }
  };

  const bulkAddToFavorites = async () => {
    if (selectedArticles.length === 0) {
      toast.error("No articles selected");
      return;
    }

    try {
      const response = await axios.post(`${API}/bulk/favorites/add`, selectedArticles);
      toast.success(response.data.message);
      setSelectedArticles([]);
      setBulkMode(false);
      fetchArticles(activeTab, page);
    } catch (error) {
      console.error("Error adding to favorites:", error);
      toast.error("Failed to add to favorites");
    }
  };

  const handleExport = async (format) => {
    try {
      const params = new URLSearchParams();
      params.append("format", format);
      // When filter is active, export from all sources, otherwise export from current tab
      if (!filterTag && !favoritesOnly) {
        params.append("source", activeTab);
      }
      if (favoritesOnly) {
        params.append("favorites_only", "true");
      }

      const response = await axios.get(`${API}/export?${params.toString()}`);
      
      if (format === "json") {
        const blob = new Blob([JSON.stringify(response.data.data, null, 2)], { type: "application/json" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `articles-${Date.now()}.json`;
        a.click();
      } else if (format === "csv") {
        const blob = new Blob([response.data.data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `articles-${Date.now()}.csv`;
        a.click();
      }
      
      toast.success(`Exported ${response.data.count} articles`);
      setShowExportDialog(false);
    } catch (error) {
      console.error("Error exporting:", error);
      toast.error("Failed to export articles");
    }
  };

  // Helper to get dynamic grid classes based on column count
  const getGridClasses = () => {
    const gridClasses = {
      2: 'grid-cols-1 sm:grid-cols-2',
      3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
      4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
      5: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5',
      6: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6'
    };
    return gridClasses[columnCount] || gridClasses[4]; // Default to 4 if invalid
  };

  // Skeleton card component with shimmer animation
  const SkeletonCard = () => (
    <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-md border border-gray-200 dark:border-gray-700">
      <div className="aspect-video bg-gray-200 dark:bg-gray-700 relative overflow-hidden">
        <div className="absolute inset-0 animate-shimmer" />
      </div>
      <div className="p-4 space-y-3">
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 relative overflow-hidden">
          <div className="absolute inset-0 animate-shimmer" />
        </div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 relative overflow-hidden">
          <div className="absolute inset-0 animate-shimmer" />
        </div>
        <div className="flex gap-2">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded-full w-16 relative overflow-hidden">
            <div className="absolute inset-0 animate-shimmer" />
          </div>
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded-full w-20 relative overflow-hidden">
            <div className="absolute inset-0 animate-shimmer" />
          </div>
        </div>
      </div>
    </div>
  );

  // Helper function to render article grid
  const renderArticleGrid = () => {
    // Show skeleton loading when loading and no articles yet
    if (loading && articles.length === 0) {
      return (
        <div className={`grid ${getGridClasses()} gap-6`} data-testid="loading-skeleton">
          {[...Array(8)].map((_, index) => (
            <SkeletonCard key={index} />
          ))}
        </div>
      );
    }
    
    // Show "no articles" only when not loading and no articles
    if (!loading && articles.length === 0) {
      return (
        <div className="text-center py-20" data-testid="no-articles">
          <ImageIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h2 className="text-2xl font-semibold text-gray-700 dark:text-gray-300 mb-2">No articles found</h2>
          <p className="text-gray-500">Try different search terms or check back later</p>
        </div>
      );
    }
    
    // Show loading overlay when fetching more articles (pagination)
    if (loading && articles.length > 0) {
      return (
        <>
          <div className={`grid ${getGridClasses()} gap-6 opacity-50`} data-testid="articles-grid">
            {articles.map((article, index) => (
              <div
                key={`${article.id}-${index}`}
                className="group bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-md hover:shadow-2xl transform hover:-translate-y-1 border border-gray-200 dark:border-gray-700 relative"
                style={{ transition: "all 0.3s ease" }}
                data-testid={`article-card-${index}`}
              >
                <Link 
                  to={`/article/${article.id}`}
                  state={{ returnPath: `/?tab=${activeTab}&page=${page}${searchQuery ? `&search=${searchQuery}` : ''}${sortBy !== 'created_at' ? `&sortBy=${sortBy}` : ''}${sortOrder !== 'desc' ? `&sortOrder=${sortOrder}` : ''}${filterTag ? `&tag=${filterTag}` : ''}${favoritesOnly ? '&favorites=true' : ''}` }}
                  className="block cursor-pointer"
                >
                  <div className="aspect-video bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 relative overflow-hidden flex items-center justify-center">
                    {article.thumbnail ? (
                      <img
                        src={processImageUrlSync(article.thumbnail, article.source)}
                        alt={article.title}
                        className={`w-full h-full object-contain group-hover:scale-105 ${nsfwMode ? 'nsfw-blur' : ''}`}
                        style={{ transition: "transform 0.5s ease" }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <ImageIcon className="w-12 h-12 text-gray-400" />
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-800 dark:text-gray-200 line-clamp-2 group-hover:text-blue-600 dark:group-hover:text-blue-400">
                      {article.title}
                    </h3>
                    <div className="mt-2 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                      <span className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full text-xs">
                        {article.source}
                      </span>
                      {article.image_count > 0 && (
                        <span className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-2 py-1 rounded-full text-xs flex items-center gap-1">
                          <ImageIcon className="w-3 h-3" />
                          {article.image_count}
                        </span>
                      )}
                    </div>
                  </div>
                </Link>

                {/* Favorite & Bulk Selection */}
                <div className="absolute top-2 right-2 flex gap-2">
                  {bulkMode && (
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleArticleSelection(article.id);
                      }}
                      className="bg-white dark:bg-gray-800 rounded-full p-2 shadow-lg cursor-pointer hover:scale-110 transition-transform"
                    >
                      {selectedArticles.includes(article.id) ? (
                        <CheckSquare className="w-5 h-5 text-blue-600" />
                      ) : (
                        <Square className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  )}
                  <div
                    onClick={(e) => toggleFavorite(article.id, e)}
                    className="bg-white dark:bg-gray-800 rounded-full p-2 shadow-lg cursor-pointer hover:scale-110 transition-transform"
                  >
                    <Heart
                      className={`w-5 h-5 ${
                        article.is_favorite
                          ? "fill-red-500 text-red-500"
                          : "text-gray-400"
                      }`}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Pagination Controls */}
          <div className="flex justify-center items-center gap-4 mt-12">
            <Button
              onClick={handlePrevPage}
              disabled={loading || page <= 1}
              variant="outline"
              size="lg"
              className="px-6 py-6 text-base rounded-full text-gray-700 dark:text-gray-300"
              data-testid="prev-page-button"
            >
              Previous Page
            </Button>
            <span className="text-gray-700 dark:text-gray-300 font-medium">
              Page {page}
            </span>
            <Button
              onClick={handleNextPage}
              disabled={loading}
              size="lg"
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-6 text-base rounded-full shadow-lg"
              data-testid="next-page-button"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                "Next Page"
              )}
            </Button>
          </div>
        </>
      );
    }
    
    return (
      <>
        <div className={`grid ${getGridClasses()} gap-6`} data-testid="articles-grid">
          {articles.map((article, index) => (
            <div
              key={`${article.id}-${index}`}
              className="group bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-md hover:shadow-2xl transform hover:-translate-y-1 border border-gray-200 dark:border-gray-700 relative"
              style={{ transition: "all 0.3s ease" }}
              data-testid={`article-card-${index}`}
            >
              <Link 
                to={`/article/${article.id}`}
                state={{ returnPath: `/?tab=${activeTab}&page=${page}${searchQuery ? `&search=${searchQuery}` : ''}${sortBy !== 'created_at' ? `&sortBy=${sortBy}` : ''}${sortOrder !== 'desc' ? `&sortOrder=${sortOrder}` : ''}${filterTag ? `&tag=${filterTag}` : ''}${favoritesOnly ? '&favorites=true' : ''}` }}
                className="block cursor-pointer"
              >
                <div className="aspect-video bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 relative overflow-hidden flex items-center justify-center">
                  {article.thumbnail ? (
                    <img
                      src={processImageUrlSync(article.thumbnail, article.source)}
                      alt={article.title}
                      className={`w-full h-full object-contain group-hover:scale-105 ${nsfwMode ? 'nsfw-blur' : ''}`}
                      style={{ transition: "transform 0.5s ease" }}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="w-12 h-12 text-gray-400" />
                    </div>
                  )}
                  <div className="absolute top-2 right-2">
                    <span className="px-3 py-1 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm rounded-full text-xs font-semibold text-gray-700 dark:text-gray-300">
                      {article.source}
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 line-clamp-2 group-hover:text-blue-600 dark:group-hover:text-blue-400" style={{ transition: "color 0.3s ease" }}>
                    {article.title}
                  </h3>
                  
                  {/* Collections - Compact Display */}
                  {article.collections && article.collections.length > 0 && (
                    <div className="flex items-center gap-1 mt-2">
                      <Folder className="w-3 h-3 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                      <div className="flex items-center gap-1 overflow-hidden">
                        <span className="text-xs text-purple-700 dark:text-purple-300 truncate">
                          {article.collections[0]}
                        </span>
                        {article.collections.length > 1 && (
                          <span className="text-xs text-purple-600 dark:text-purple-400 font-semibold flex-shrink-0">
                            +{article.collections.length - 1}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Tags */}
                  {article.tags && article.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {article.tags.slice(0, 2).map((tag, idx) => (
                        <span key={idx} className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-xs rounded-full">
                          {tag}
                        </span>
                      ))}
                      {article.tags.length > 2 && (
                        <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded-full">
                          +{article.tags.length - 2}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </Link>
              
              {/* Action Buttons */}
              {!bulkMode && (
                <div className="absolute top-2 left-2 flex gap-2">
                  {/* Favorite Button */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm hover:bg-white dark:hover:bg-gray-900"
                    onClick={(e) => toggleFavorite(article.id, e)}
                    data-testid={`favorite-button-${index}`}
                  >
                    <Heart 
                      className={`w-5 h-5 ${article.is_favorite ? 'fill-red-500 text-red-500' : 'text-gray-600 dark:text-gray-400'}`}
                    />
                  </Button>
                  
                  {/* Quick Add to Collection Button */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm hover:bg-white dark:hover:bg-gray-900"
                    onClick={(e) => {
                      e.stopPropagation();
                      setQuickAddCollectionArticleId(article.id);
                      setShowQuickAddDialog(true);
                    }}
                    title="Add to collection"
                  >
                    <FolderPlus className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </Button>
                </div>
              )}

              {/* Bulk Select Checkbox */}
              {bulkMode && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-2 left-2 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm hover:bg-white dark:hover:bg-gray-900"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleArticleSelection(article.id);
                  }}
                >
                  {selectedArticles.includes(article.id) ? (
                    <CheckSquare className="w-5 h-5 text-blue-600" />
                  ) : (
                    <Square className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  )}
                </Button>
              )}
            </div>
          ))}
        </div>

        {/* Pagination Controls */}
        <div className="flex justify-center items-center gap-4 mt-12">
          <Button
            onClick={handlePrevPage}
            disabled={loading || page <= 1}
            variant="outline"
            size="lg"
            className="px-6 py-6 text-base rounded-full text-gray-700 dark:text-gray-300"
          >
            Previous Page
          </Button>
          <span className="text-gray-700 dark:text-gray-300 font-medium">
            Page {page}
          </span>
          <Button
            onClick={handleNextPage}
            disabled={loading}
            size="lg"
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-6 text-base rounded-full shadow-lg"
            data-testid="next-page-button"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Loading...
              </>
            ) : (
              "Next Page"
            )}
          </Button>
        </div>
      </>
    );
  };

  // Removed: const currentTab = getCurrentTabState(); - using direct state variables instead

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Compact Header - Optimized for More Content Space */}
      <header className="relative bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-2">
          <div className="flex items-center justify-between gap-2">
            {/* Left: Compact Title + Search */}
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <h1 className="text-base sm:text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent whitespace-nowrap" data-testid="app-title">
                Gallery
              </h1>
              
              {/* Inline Compact Search */}
              <div className="flex-1 flex items-center gap-1 max-w-md">
                <div className="relative flex-1">
                  <Input
                    placeholder={`Search ${activeTab}...`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                    className="text-xs h-8 pr-8"
                    data-testid="search-input"
                  />
                  {isSearchMode && (
                    <button
                      onClick={clearSearch}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      title="Clear search"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
                <Button
                  onClick={handleSearch}
                  disabled={loading || !searchQuery.trim()}
                  data-testid="search-button"
                  className="bg-blue-600 hover:bg-blue-700 h-8 w-8 p-0"
                  size="icon"
                >
                  {loading ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Search className="w-3 h-3" />
                  )}
                </Button>
              </div>
            </div>

            {/* Right: Compact Actions */}
            <div className="flex items-center gap-1">
              {/* NSFW Toggle */}
              <div className="flex items-center gap-1" data-testid="nsfw-toggle-container">
                <Switch
                  id="nsfw-mode"
                  checked={nsfwMode}
                  onCheckedChange={handleNsfwToggle}
                  data-testid="nsfw-toggle"
                  className="scale-75"
                />
                <Label htmlFor="nsfw-mode" className="text-xs cursor-pointer hidden lg:inline">NSFW</Label>
              </div>
              
              {/* Filters Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowFilters(!showFilters)}
                className="h-8 w-8 hover:bg-gray-100 dark:hover:bg-gray-800"
                title="Filters"
              >
                <Filter className={`w-4 h-4 ${showFilters ? 'text-blue-600' : ''}`} />
              </Button>
              
              {/* Navigation Icons */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/favorites")}
                className="h-8 w-8 hover:bg-gray-100 dark:hover:bg-gray-800"
                title="Favorites"
              >
                <Heart className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/collections")}
                className="h-8 w-8 hover:bg-gray-100 dark:hover:bg-gray-800 hidden sm:inline-flex"
                title="Collections"
              >
                <FolderOpen className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/statistics")}
                className="h-8 w-8 hover:bg-gray-100 dark:hover:bg-gray-800 hidden md:inline-flex"
                title="Statistics"
              >
                <BarChart3 className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/settings")}
                className="h-8 w-8 hover:bg-gray-100 dark:hover:bg-gray-800 hidden md:inline-flex"
                title="Settings"
              >
                <SettingsIcon className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="h-8 w-8 hover:bg-gray-100 dark:hover:bg-gray-800"
                title="Toggle theme"
              >
                {theme === "light" ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          {/* Search Mode Indicator - Compact */}
          {isSearchMode && (
            <div className="mt-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded px-2 py-1">
              <p className="text-xs text-blue-700 dark:text-blue-300">
                🔍 <strong>"{searchQuery}"</strong> in <strong>{activeTab}</strong>
                <button 
                  onClick={clearSearch}
                  className="ml-2 underline hover:no-underline"
                >
                  clear
                </button>
              </p>
            </div>
          )}

          {/* Collapsible Filters - Compact */}
          {showFilters && (
            <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-800/50 rounded border border-gray-200 dark:border-gray-700">
              <div className="flex flex-wrap gap-2 items-center text-xs">
                {/* Tag Filter */}
                <Select value={filterTag || "all-tags"} onValueChange={(value) => { 
                  const newTag = value === "all-tags" ? "" : value;
                  setFilterTag(newTag);
                }}>
                  <SelectTrigger className="w-[100px] h-7 text-xs">
                    <SelectValue placeholder="Tag" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all-tags">All Tags</SelectItem>
                    {allTags.map((tag) => (
                      <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Favorites Filter */}
                <div className="flex items-center gap-1">
                  <Switch
                    id="favorites-filter"
                    checked={favoritesOnly}
                    onCheckedChange={(checked) => { setFavoritesOnly(checked); }}
                    className="scale-75"
                  />
                  <Label htmlFor="favorites-filter" className="text-xs cursor-pointer">Favorites</Label>
                </div>

                {/* Sort By */}
                <Select value={sortBy} onValueChange={(value) => { setSortBy(value); }}>
                  <SelectTrigger className="w-[80px] h-7 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="created_at">Date</SelectItem>
                    <SelectItem value="title">Title</SelectItem>
                  </SelectContent>
                </Select>

                {/* Sort Order */}
                <Select value={sortOrder} onValueChange={(value) => { setSortOrder(value); }}>
                  <SelectTrigger className="w-[70px] h-7 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="desc">↓</SelectItem>
                    <SelectItem value="asc">↑</SelectItem>
                  </SelectContent>
                </Select>

                {/* Divider */}
                <div className="h-4 w-px bg-gray-300 dark:bg-gray-600"></div>

                {/* Bulk Actions */}
                <Button
                  variant={bulkMode ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setBulkMode(!bulkMode);
                    setSelectedArticles([]);
                  }}
                  className="h-7 text-xs px-2"
                >
                  <CheckSquare className="w-3 h-3 mr-1" />
                  Bulk
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowExportDialog(true)}
                  className="h-7 text-xs px-2 hidden sm:inline-flex"
                >
                  <Download className="w-3 h-3 mr-1" />
                  Export
                </Button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Tab Bar - Separate for conditional sticky */}
      <div className={`${stickyTabBar ? 'sticky top-0 z-40' : 'relative'} bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
          <Tabs value={activeTab} onValueChange={(value) => {
            setActiveTab(value);
            setPage(1); // Reset page when switching tabs
            setSearchQuery(""); // Clear search when switching tabs
            setIsSearchMode(false);
            
            // Update URL to trigger fetch via useEffect
            updateURLParams({
              tab: value,
              page: 1,
              search: null,
              sortBy: sortBy !== 'created_at' ? sortBy : null,
              sortOrder: sortOrder !== 'desc' ? sortOrder : null,
              tag: filterTag || null,
              favorites: favoritesOnly || null
            });
          }}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="misskon" className="text-sm sm:text-base">
                Misskon
              </TabsTrigger>
              <TabsTrigger value="4khd" className="text-sm sm:text-base">
                4KHD
              </TabsTrigger>
              <TabsTrigger value="06se" className="text-sm sm:text-base">
                06SE
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Bulk Actions Toolbar - Mobile Optimized */}
      {bulkMode && selectedArticles.length > 0 && (
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-2">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-2 sm:p-3">
            <div className="flex flex-col sm:flex-row items-start sm:items-center sm:justify-between gap-2">
              <span className="text-xs sm:text-sm font-medium text-blue-900 dark:text-blue-100">
                {selectedArticles.length} selected
              </span>
              <div className="flex flex-wrap gap-1 sm:gap-2 w-full sm:w-auto">
                <Button size="sm" variant="outline" onClick={bulkAddToFavorites} className="text-xs h-7 sm:h-8 flex-1 sm:flex-none text-gray-700 dark:text-gray-300">
                  <Heart className="w-3 h-3 mr-1" />
                  Favorites
                </Button>
                <Button size="sm" variant="outline" onClick={bulkAddTag} className="text-xs h-7 sm:h-8 flex-1 sm:flex-none text-gray-700 dark:text-gray-300">
                  <TagIcon className="w-3 h-3 mr-1" />
                  Tag
                </Button>
                <Select onValueChange={bulkAddToCollection}>
                  <SelectTrigger className="w-full sm:w-[140px] h-7 sm:h-8 text-xs">
                    <SelectValue placeholder="Collection" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="create_new" className="text-blue-600 dark:text-blue-400 font-semibold">
                      + Create New...
                    </SelectItem>
                    {collections.map((col) => (
                      <SelectItem key={col.id} value={col.id}>{col.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" variant="ghost" onClick={() => { setSelectedArticles([]); setBulkMode(false); }} className="text-xs h-7 sm:h-8">
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Export Articles</DialogTitle>
            <DialogDescription>
              Choose export format for current filtered articles
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 mt-4">
            <Button onClick={() => handleExport("json")} className="w-full">
              Export as JSON
            </Button>
            <Button onClick={() => handleExport("csv")} variant="outline" className="w-full">
              Export as CSV
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create New Collection Dialog (for Bulk Actions) */}
      <Dialog open={showCreateCollectionDialog} onOpenChange={(open) => {
        setShowCreateCollectionDialog(open);
        if (!open) {
          setNewCollectionName("");
          setNewCollectionDesc("");
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Collection</DialogTitle>
            <DialogDescription>
              Create a new collection and add {selectedArticles.length} selected articles to it
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Collection Name *</label>
              <Input
                placeholder="Enter collection name"
                value={newCollectionName}
                onChange={(e) => setNewCollectionName(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && createBulkCollection()}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Description (optional)</label>
              <Input
                placeholder="Enter description"
                value={newCollectionDesc}
                onChange={(e) => setNewCollectionDesc(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowCreateCollectionDialog(false)} 
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={createBulkCollection} 
                className="flex-1"
                disabled={!newCollectionName.trim()}
              >
                Create & Add Articles
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Add to Collection Dialog */}
      <Dialog open={showQuickAddDialog} onOpenChange={setShowQuickAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add to Collection</DialogTitle>
            <DialogDescription>
              Choose a collection to add this article to
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {/* Create New Collection Button */}
            <Button
              variant="outline"
              className="w-full justify-start text-left h-auto py-3 border-2 border-dashed border-blue-300 dark:border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
              onClick={() => quickAddToCollection("create_new")}
            >
              <div className="flex items-start gap-3 w-full">
                <FolderPlus className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-blue-600 dark:text-blue-400">Create New Collection</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Create a new collection and add this article
                  </p>
                </div>
              </div>
            </Button>
            
            {collections.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FolderOpen className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                <p>No collections yet</p>
                <p className="text-sm">Create your first collection above</p>
              </div>
            ) : (
              collections.map((collection) => (
                <Button
                  key={collection.id}
                  variant="outline"
                  className="w-full justify-start text-left h-auto py-3"
                  onClick={() => quickAddToCollection(collection.id)}
                >
                  <div className="flex items-start gap-3 w-full">
                    <Folder className="w-5 h-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">{collection.name}</p>
                      {collection.description && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                          {collection.description}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        {collection.article_count || 0} articles
                      </p>
                    </div>
                  </div>
                </Button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Quick Collection Dialog */}
      <Dialog open={showCreateQuickCollectionDialog} onOpenChange={setShowCreateQuickCollectionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Collection</DialogTitle>
            <DialogDescription>
              Create a new collection and add this article to it
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="quickCollectionName">Collection Name *</Label>
              <Input
                id="quickCollectionName"
                placeholder="Enter collection name"
                value={quickCollectionName}
                onChange={(e) => setQuickCollectionName(e.target.value)}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="quickCollectionDesc">Description (Optional)</Label>
              <Input
                id="quickCollectionDesc"
                placeholder="Enter description"
                value={quickCollectionDesc}
                onChange={(e) => setQuickCollectionDesc(e.target.value)}
                className="mt-1.5"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCreateQuickCollectionDialog(false);
                  setQuickCollectionName("");
                  setQuickCollectionDesc("");
                  setShowQuickAddDialog(true);
                }}
                className="flex-1"
              >
                Back
              </Button>
              <Button
                onClick={createQuickCollection}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                Create & Add
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Crawl Status Banner */}
      {crawlStatus.status === 'crawling' && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <div className="bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Loader2 className="w-5 h-5 animate-spin text-blue-600 dark:text-blue-400" />
              <div>
                <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  Đang crawl articles từ 06se.com...
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">
                  Progress: {crawlStatus.progress}% - {crawlStatus.articles_count} articles đã tìm thấy
                </p>
              </div>
            </div>
            <div className="w-48 bg-blue-200 dark:bg-blue-700 rounded-full h-2">
              <div 
                className="bg-blue-600 dark:bg-blue-400 h-2 rounded-full transition-all duration-500"
                style={{ width: `${crawlStatus.progress}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Main Content with Tab Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab}>
          
          <TabsContent value="misskon">
            {renderArticleGrid()}
          </TabsContent>
          
          <TabsContent value="4khd">
            {renderArticleGrid()}
          </TabsContent>
          
          <TabsContent value="06se">
            {renderArticleGrid()}
          </TabsContent>
        </Tabs>
      </main>
      
      {/* Notification Banner */}
      <NotificationBanner />
      
      {/* Global Loading Overlay - Fixed position, always on top */}
      {loading && articles.length > 0 && (
        <div className="fixed inset-0 flex items-center justify-center bg-white/30 dark:bg-gray-900/30 backdrop-blur-sm z-[9999]">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 flex flex-col items-center gap-4 border border-gray-200 dark:border-gray-700">
            <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
            <p className="text-lg font-semibold text-gray-700 dark:text-gray-300">Loading articles...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;